import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import MicIcon from '@material-ui/icons/Mic'
import MicOffIcon from '@material-ui/icons/MicOff'
import { useIntl } from 'react-intl'
import useAudioPlayer from './hooks/useAudioPlayer'
import useAzureSpeechRecognition from './hooks/useAzureSpeechRecognition'
import { CapeeshColors } from '../../../../assets/ColorPalette'
import SceneBackground from './SceneBackground'
import { playerMessages } from './CapeeshPlayer.i18n.Messages'
import {
    preProcessTextForSpeechRecognition,
    createTokensFromSnippet,
    SpeakAlternativeToken,
    associateSpeechWithAlternativeToken,
    createSnippetTracker,
    associateSpeechWithSnippetTracker,
    SpeechEvaluationResultType,
    getPolishHomophoneStrings,
    POLISH_LANGUAGE_ID,
} from './speech/speechScoringLogic'

const JACCARD_THRESHOLD = 0.65
const TIMEOUT_AFTER_INPUT_SEC = 8
const MAX_RECORDING_SEC = 20

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        padding: '12px 20px 24px',
        boxSizing: 'border-box',
        overflow: 'auto',
        '@media (min-width: 600px) and (orientation: landscape)': {
            gap: 10,
            padding: '8px 16px 16px',
        },
        '@media (max-width: 599px)': {
            padding: '4px 10px 12px',
        },
    },
    topArea: {
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        gap: 16,
        '@media (max-width: 599px)': {
            gap: 10,
        },
    },
    sceneContainer: {
        width: '100%',
        maxWidth: 520,
        flexShrink: 0,
        minHeight: 100,
        borderRadius: 16,
        '@media (min-width: 600px) and (orientation: landscape)': {
            maxWidth: 'min(520px, 45vh)',
        },
        '@media (max-width: 599px)': {
            borderRadius: 10,
        },
    },

    // Mic status badge
    micBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 16px',
        borderRadius: 20,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(8px)',
        flexShrink: 0,
    },
    micBadgeRecording: {
        backgroundColor: 'rgba(255, 81, 89, 0.15)',
    },
    micIcon: {
        fontSize: 20,
        color: 'rgba(255, 255, 255, 0.6)',
    },
    micIconRecording: {
        color: CapeeshColors.PinkBright,
        animation: '$pulse 1.5s ease-in-out infinite',
    },
    micText: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.6)',
    },
    micTextRecording: {
        color: CapeeshColors.PinkBright,
    },

    // Options
    optionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
        maxWidth: 480,
        flexShrink: 0,
        '@media (min-width: 600px) and (orientation: landscape)': {
            gap: 8,
        },
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            gap: 8,
        },
    },
    optionButton: {
        width: '100%',
        padding: '16px 20px',
        borderRadius: 16,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(8px)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 500,
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 300ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
        },
        '@media (min-width: 600px) and (orientation: landscape)': {
            fontSize: 14,
            padding: '12px 16px',
        },
        '@media (max-width: 599px)': {
            padding: '12px 14px',
            fontSize: 14,
            borderRadius: 12,
        },
    },
    optionCorrect: {
        borderColor: CapeeshColors.GreenBright,
        backgroundColor: 'rgba(128, 211, 135, 0.25)',
        animation: '$popIn 350ms ease',
        '&:hover': {
            backgroundColor: 'rgba(128, 211, 135, 0.25)',
            borderColor: CapeeshColors.GreenBright,
        },
    },
    optionIncorrect: {
        borderColor: CapeeshColors.PinkBright,
        backgroundColor: 'rgba(255, 102, 110, 0.25)',
        animation: '$popIn 350ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 102, 110, 0.25)',
            borderColor: CapeeshColors.PinkBright,
        },
    },
    optionDimmed: {
        opacity: 0.35,
        cursor: 'default',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
        },
    },
    optionExamSelected: {
        borderColor: 'rgba(255, 255, 255, 0.5)',
        backgroundColor: 'rgba(255, 255, 255, 0.18)',
    },

    feedbackText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'center',
        flexShrink: 0,
        '@media (min-width: 600px) and (orientation: landscape)': {
            fontSize: 12,
        },
    },
    feedbackCorrect: {
        color: CapeeshColors.GreenBright,
    },
    feedbackIncorrect: {
        color: CapeeshColors.PinkBright,
    },

    // Bottom section
    bottomArea: {
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0,
        width: '100%',
        padding: '20px 0 20px',
        boxSizing: 'border-box',
        '@media (min-width: 600px) and (orientation: landscape)': {
            padding: '12px 0 20px',
        },
        '@media (max-width: 599px)': {
            padding: '12px 16px 20px',
        },
    },
    skipButton: {
        padding: '14px 48px',
        borderRadius: 28,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'transparent',
        color: 'rgba(255, 255, 255, 0.7)',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 200ms ease',
        width: '100%',
        maxWidth: 480,
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            color: '#FFFFFF',
        },
    },
    skipButtonProminent: {
        backgroundColor: '#FFFFFF',
        color: CapeeshColors.Green,
        border: 'none',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
        '&:hover': {
            backgroundColor: '#FFFFFF',
            color: CapeeshColors.Green,
            border: 'none',
            transform: 'translateY(-1px)',
        },
    },

    '@keyframes pulse': {
        '0%, 100%': { transform: 'scale(1)', opacity: 1 },
        '50%': { transform: 'scale(1.15)', opacity: 0.7 },
    },
    '@keyframes popIn': {
        '0%': { transform: 'scale(0.97)' },
        '50%': { transform: 'scale(1.02)' },
        '100%': { transform: 'scale(1)' },
    },
}))

const SpeakNode = ({ node, previousNode, imageMap, audioMap, onNodeComplete, isExamMode, l2LanguageId, lessonDto }) => {
    const classes = useStyles()
    const intl = useIntl()
    const audioPlayer = useAudioPlayer()
    const speech = useAzureSpeechRecognition(l2LanguageId)

    const [selectedIndex, setSelectedIndex] = useState(null)
    const [showFeedback, setShowFeedback] = useState(false)
    const [isListening, setIsListening] = useState(false)
    const [timedOut, setTimedOut] = useState(false)

    const allRecognizedTextRef = useRef('')
    const foundMatchRef = useRef(false)
    const timeoutTimerRef = useRef(null)
    const inputTimeoutRef = useRef(null)
    const hasAdvancedRef = useRef(false)

    const animationDuration = node.scenarioMentors?.[0]?.animationLengthInSeconds || 1.5
    const options = node.options || []

    const shuffledOrder = useMemo(() => {
        const indices = options.map((_, i) => i)
        for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[indices[i], indices[j]] = [indices[j], indices[i]]
        }
        return indices
    }, [options.length])

    const isCorrectOption = (opt) => opt.scoreAlteration >= 0 && !opt.failure

    const firstCorrectIndex = useMemo(() => {
        return options.findIndex((opt) => isCorrectOption(opt))
    }, [options])

    // Build evaluation targets for each option
    const evaluationTargets = useMemo(() => {
        const snippetTrackers = []
        const alternativeTokens = []
        const speechEvaluationSnippets = lessonDto?.speechEvaluationSnippets || []
        const isPolish = l2LanguageId === POLISH_LANGUAGE_ID

        for (let i = 0; i < options.length; i++) {
            const opt = options[i]
            if (!opt.text) continue

            const snippet = speechEvaluationSnippets.find(
                (s) => s.snippetId === (opt.textSnippetId || '')
            )

            if (snippet) {
                snippetTrackers.push(createSnippetTracker(snippet, i))
            } else {
                const trimmedText = preProcessTextForSpeechRecognition(opt.text)
                if (trimmedText) {
                    alternativeTokens.push(new SpeakAlternativeToken(trimmedText, i))
                    if (isPolish) {
                        for (const variant of getPolishHomophoneStrings(opt.text)) {
                            const processed = preProcessTextForSpeechRecognition(variant)
                            if (processed && processed !== trimmedText) {
                                alternativeTokens.push(new SpeakAlternativeToken(processed, i))
                            }
                        }
                    }
                }
            }
        }
        return { snippetTrackers, alternativeTokens }
    }, [options, lessonDto?.speechEvaluationSnippets, l2LanguageId])

    // Evaluate speech against all options; returns the best matching option index or -1
    const evaluateSpeech = useCallback(
        (speechText, isFinal) => {
            const { snippetTrackers, alternativeTokens } = evaluationTargets

            let fullText
            if (isFinal) {
                if (allRecognizedTextRef.current) {
                    allRecognizedTextRef.current += ' ' + speechText
                } else {
                    allRecognizedTextRef.current = speechText
                }
                fullText = allRecognizedTextRef.current
            } else {
                fullText = allRecognizedTextRef.current
                    ? `${allRecognizedTextRef.current} ${speechText}`
                    : speechText
            }

            if (process.env.NODE_ENV !== 'production') {
                console.log('[SpeakNode] evaluateSpeech:', { speechText, isFinal, fullText, snippetCount: snippetTrackers.length, altTokenCount: alternativeTokens.length })
            }

            let bestIdx = -1
            let bestScore = -1

            // Path 1: snippet trackers
            if (snippetTrackers.length > 0) {
                const tokens = createTokensFromSnippet(fullText, l2LanguageId)
                if (tokens.length > 0) {
                    for (const tracker of snippetTrackers) {
                        const result = associateSpeechWithSnippetTracker(tokens, tracker)
                        if (
                            result.result !== SpeechEvaluationResultType.NotPassed &&
                            result.score > bestScore
                        ) {
                            bestScore = result.score
                            bestIdx = tracker.alternativeIdx
                        }
                    }
                }
            }

            // Path 2: alternative tokens
            if (alternativeTokens.length > 0) {
                const words = fullText.split(/\s+/).filter(Boolean)
                if (words.length > 0) {
                    for (const token of alternativeTokens) {
                        const result = associateSpeechWithAlternativeToken(
                            words, token, JACCARD_THRESHOLD, false
                        )
                        if (
                            result.result !== SpeechEvaluationResultType.NotPassed &&
                            result.score > bestScore
                        ) {
                            bestScore = result.score
                            bestIdx = token.alternativeIdx
                        }
                    }
                }
            }

            if (process.env.NODE_ENV !== 'production') {
                console.log('[SpeakNode] evaluateSpeech result:', { bestIdx, bestScore })
            }

            return bestIdx
        },
        [evaluationTargets, l2LanguageId]
    )

    const advanceWithOption = useCallback(
        (optionIndex, { skipped = false } = {}) => {
            if (hasAdvancedRef.current) return
            hasAdvancedRef.current = true
            speech.stopRecognition()
            audioPlayer.stopAudio()
            clearTimeout(timeoutTimerRef.current)
            clearTimeout(inputTimeoutRef.current)
            setIsListening(false)

            setSelectedIndex(optionIndex)
            setShowFeedback(true)

            const opt = options[optionIndex]
            const optAudioUrl = opt?.textAudioUrl ? audioMap[opt.textAudioUrl] : null
            if (optAudioUrl) {
                audioPlayer.playAudio(optAudioUrl)
            }

            setTimeout(() => {
                audioPlayer.stopAudio()
                onNodeComplete({
                    nodeId: node.nodeId,
                    chosenAlternativeIndex: optionIndex,
                    skipped,
                })
            }, 1500)
        },
        [options, audioMap, audioPlayer, node.nodeId, onNodeComplete, speech]
    )

    const handleSpeechMatch = useCallback(
        (optionIndex) => {
            if (foundMatchRef.current || hasAdvancedRef.current) return
            foundMatchRef.current = true
            advanceWithOption(optionIndex)
        },
        [advanceWithOption]
    )

    const handleTimeout = useCallback(() => {
        if (foundMatchRef.current || hasAdvancedRef.current) return
        speech.stopRecognition()
        setIsListening(false)
        setTimedOut(true)
    }, [speech])

    // Reset the after-input timeout whenever speech comes in
    const resetInputTimeout = useCallback(() => {
        clearTimeout(inputTimeoutRef.current)
        inputTimeoutRef.current = setTimeout(() => {
            handleTimeout()
        }, TIMEOUT_AFTER_INPUT_SEC * 1000)
    }, [handleTimeout])

    // Start recording on mount
    useEffect(() => {
        const promptAudioUrl = node.backgroundAudioUrl
            ? audioMap[node.backgroundAudioUrl]
            : null

        const startListening = async () => {
            if (promptAudioUrl) {
                await audioPlayer.playAudio(promptAudioUrl)
            }

            const started = await speech.startRecognition({
                onRecognizing: (text) => {
                    if (foundMatchRef.current || hasAdvancedRef.current) return
                    const processed = preProcessTextForSpeechRecognition(text)
                    resetInputTimeout()
                    const matchIdx = evaluateSpeech(processed, false)
                    if (matchIdx >= 0) handleSpeechMatch(matchIdx)
                },
                onRecognized: (text) => {
                    if (foundMatchRef.current || hasAdvancedRef.current) return
                    const processed = preProcessTextForSpeechRecognition(text)
                    resetInputTimeout()
                    const matchIdx = evaluateSpeech(processed, true)
                    if (matchIdx >= 0) handleSpeechMatch(matchIdx)
                },
                onError: () => {
                    setIsListening(false)
                    setTimedOut(true)
                },
            })

            if (started) {
                setIsListening(true)
                // Hard max timeout
                timeoutTimerRef.current = setTimeout(() => {
                    handleTimeout()
                }, MAX_RECORDING_SEC * 1000)
            } else {
                setTimedOut(true)
            }
        }

        startListening()

        return () => {
            speech.stopRecognition()
            audioPlayer.stopAudio()
            clearTimeout(timeoutTimerRef.current)
            clearTimeout(inputTimeoutRef.current)
        }
    }, [])

    // Tap-to-select fallback
    const handleTapSelect = useCallback(
        (originalIndex) => {
            if (selectedIndex !== null || hasAdvancedRef.current) return
            foundMatchRef.current = true
            advanceWithOption(originalIndex)
        },
        [selectedIndex, advanceWithOption]
    )

    const handleSkip = useCallback(() => {
        if (hasAdvancedRef.current) return
        const idx = firstCorrectIndex >= 0 ? firstCorrectIndex : 0
        foundMatchRef.current = true
        advanceWithOption(idx, { skipped: true })
    }, [firstCorrectIndex, advanceWithOption])

    const selectedOption = selectedIndex !== null ? options[selectedIndex] : null
    const isSelectedCorrect = selectedOption ? isCorrectOption(selectedOption) : null
    const locked = selectedIndex !== null

    useEffect(() => {
        if (locked) return
        const onKey = (e) => {
            if (e.key === 'Enter') handleSkip()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [locked, handleSkip])

    return (
        <div className={classes.container}>
            <div className={classes.topArea}>
                <div className={classes.sceneContainer}>
                    <SceneBackground
                        node={node}
                        previousNode={previousNode}
                        imageMap={imageMap}
                        duration={animationDuration}
                    />
                </div>

                {/* Mic recording indicator */}
                <div
                    className={`${classes.micBadge} ${isListening ? classes.micBadgeRecording : ''}`}
                >
                    {isListening ? (
                        <MicIcon className={`${classes.micIcon} ${classes.micIconRecording}`} />
                    ) : (
                        <MicOffIcon className={classes.micIcon} />
                    )}
                    <Typography
                        className={`${classes.micText} ${isListening ? classes.micTextRecording : ''}`}
                    >
                        {isListening ? intl.formatMessage(playerMessages.listening) : timedOut ? intl.formatMessage(playerMessages.tapAnswerOrSkip) : intl.formatMessage(playerMessages.startingMic)}
                    </Typography>
                </div>

                {/* Answer options */}
                <div className={classes.optionsContainer}>
                    {shuffledOrder.map((origIdx) => {
                        const opt = options[origIdx]
                        if (!opt) return null

                        let extraClass = ''
                        if (showFeedback) {
                            if (isExamMode) {
                                if (origIdx === selectedIndex) {
                                    extraClass = classes.optionExamSelected
                                }
                            } else {
                                if (origIdx === selectedIndex) {
                                    extraClass = isCorrectOption(opt)
                                        ? classes.optionCorrect
                                        : classes.optionIncorrect
                                } else if (isCorrectOption(opt) && !isSelectedCorrect) {
                                    extraClass = classes.optionCorrect
                                } else {
                                    extraClass = classes.optionDimmed
                                }
                            }
                        }

                        return (
                            <button
                                key={origIdx}
                                className={`${classes.optionButton} ${extraClass}`}
                                onClick={() => handleTapSelect(origIdx)}
                                disabled={locked}
                            >
                                {opt.text || intl.formatMessage(playerMessages.optionNumber, { number: origIdx + 1 })}
                            </button>
                        )
                    })}
                </div>

                {showFeedback && !isExamMode && (
                    <Typography
                        className={`${classes.feedbackText} ${
                            isSelectedCorrect ? classes.feedbackCorrect : classes.feedbackIncorrect
                        }`}
                    >
                        {isSelectedCorrect ? intl.formatMessage(playerMessages.correctFeedback) : intl.formatMessage(playerMessages.incorrectFeedback)}
                    </Typography>
                )}
            </div>

            {/* Skip / Continue button */}
            {!locked && (
                <div className={classes.bottomArea}>
                    <button
                        className={`${classes.skipButton} ${timedOut ? classes.skipButtonProminent : ''}`}
                        onClick={handleSkip}
                    >
                        {timedOut ? intl.formatMessage(playerMessages.continueButton) : intl.formatMessage(playerMessages.skip)}
                    </button>
                </div>
            )}
        </div>
    )
}

export default SpeakNode
